/**
 * Ported from daisyUI 5 — packages/daisyui/src/components/filter.css
 * Source: https://github.com/saadeghi/daisyui (MIT, © Pouya Saadeghi)
 * License: MIT — see LICENSE-THIRD-PARTY.md.
 *
 * daisyUI implements `.filter` as a pure-CSS pattern: a group of
 * `input[type="radio"]` plus a `.filter-reset` radio. `:has()` selectors
 * collapse the non-selected radios to zero width once one is checked, and
 * reveal the reset. ry-filter ports the BEHAVIOR (single-select chip group
 * that hides its siblings + reveals a reset) into clean JS so it works
 * without `:has()` support and exposes a real `ry:change` contract.
 *
 * @component ry-filter
 * @category  form
 * @summary   Single-select chip group with collapse-on-select. Click a chip:
 *            it becomes active, the OTHER chips collapse away, and a reset (×)
 *            control appears. Click reset: selection clears and every chip
 *            returns. Radio-group semantics on a chip surface — exactly one (or
 *            zero) chip is selected at a time.
 *
 * @attribute name          string?    Group key. Reflected onto the host; used as the logical name of the radiogroup (informational — coordination is internal, scoped to this element's own options).
 * @attribute value         string?    Selected option value. Set it to preselect; read it to get the current selection. Empty string ('') means nothing selected. Setting via attribute at mount preselects + collapses siblings.
 * @attribute size          'xs'|'sm'|'md'|'lg'|'xl'   default=md   Chip size. Drives padding + font-size on options AND the reset chip.
 * @attribute reset-label   string?    default="×"   Text/glyph for the reset chip. Use a word ("All") or keep the default × glyph.
 *
 * @property  value    string    Live selected value. Setter selects the matching option (collapsing siblings + showing reset) and fires `ry:change`. Set to '' to clear (same as reset()).
 * @property  name     string    Live group key (reflects the `name` attribute).
 *
 * @method    reset()  Clear the selection: deselect the active chip, restore all chips, hide the reset control, and fire `ry:change` with `{ value: null }`. No-op (no event) when nothing is selected.
 *
 * @event ry:beforechange
 *        detail: { value: string | null, previous: string | null }
 *        fires:  before a selection transition — chip click, Enter/Space, `el.value =`, `reset()`, Escape, the reset control — after the no-transition checks and before any chip changes state. `value` is the selection ABOUT to apply (null for a reset), `previous` the current one (null when nothing is selected). Attribute-driven re-syncs are silent and do not fire it.
 *        cancelable: preventDefault() keeps the current selection; `ry:change` does not fire.
 *
 * @event ry:change
 *        detail: { value: string | null }
 *        fires:  every time the selection transitions — on select (`value` = chosen option value) and on reset/clear (`value` = null). Does NOT fire when re-selecting the already-selected chip (no transition), nor when reset() runs with nothing selected.
 *
 * @keyboard  ArrowRight / ArrowDown — move focus to next VISIBLE option (wraps)
 *            ArrowLeft  / ArrowUp   — move focus to previous VISIBLE option (wraps)
 *            Home / End             — first / last visible option
 *            Enter / Space          — select the focused option
 *            Escape                 — reset (clear selection, restore all chips)
 *
 * @state     `data-ry-state` on the host: 'idle' (nothing selected, all chips
 *            shown, reset hidden) or 'filtered' (one chip selected, siblings
 *            collapsed, reset shown). Selected option carries
 *            `data-ry-selected` + `aria-checked="true"`.
 *
 * @content   Declarative `<ry-filter-option value="…">Label</ry-filter-option>`
 *            children. They are read ONCE at setup() and re-rendered into the
 *            host's managed markup (option chips + a trailing reset chip). After
 *            setup the original `<ry-filter-option>` elements are gone.
 *
 * @dom
 *   ry-filter[name?][value?][size?]  role="radiogroup"  data-ry-state="idle|filtered"
 *     > button[data-ry-target="option"][value][role="radio"][aria-checked]  — one per option
 *     > button[data-ry-target="reset"][aria-label="Clear filter"]           — the × reset chip
 *
 * @gotcha  Options are read ONCE at setup. Setting `.innerHTML` with new
 *          `<ry-filter-option>` markup on an already-upgraded ry-filter does
 *          NOT re-render — the host already replaced its children with managed
 *          chip buttons. To repopulate dynamically, replace the PARENT's
 *          innerHTML with a fresh `<ry-filter>…</ry-filter>` block so the
 *          parser upgrades a brand-new element with its options present.
 *
 * @gotcha  Selecting the already-selected chip is a no-op — it does NOT toggle
 *          off (radio-group semantics, mirroring daisyUI where a checked radio
 *          stays checked). To clear, use the reset chip, press Escape, or call
 *          `reset()` / set `.value = ''`.
 *
 * @gotcha  "Collapsed" siblings are still in the DOM and still
 *          `querySelectorAll`-able — they are hidden via `data-ry-state` +
 *          CSS (width/opacity → 0), not removed. Keyboard navigation skips
 *          them because they are not visible; tabindex is set to -1 while
 *          collapsed.
 *
 * @gotcha  The reset chip is rendered ALWAYS (so it can animate in), but is
 *          collapsed to zero size while `data-ry-state="idle"`. It only
 *          becomes interactive once a selection exists.
 *
 * @gotcha  `error`-style daisyUI naming is not used here; ry-filter has no
 *          color variants on individual chips — the ACTIVE chip uses
 *          `--ry-color-primary`. Per-chip coloring is out of scope (matches
 *          daisyUI's filter, which is monochrome).
 *
 * @example basic
 * ```html
 * <ry-filter name="framework">
 *   <ry-filter-option value="svelte">Svelte</ry-filter-option>
 *   <ry-filter-option value="vue">Vue</ry-filter-option>
 *   <ry-filter-option value="react">React</ry-filter-option>
 * </ry-filter>
 * ```
 *
 * @example preselected
 * ```html
 * <ry-filter name="plan" value="pro">
 *   <ry-filter-option value="free">Free</ry-filter-option>
 *   <ry-filter-option value="pro">Pro</ry-filter-option>
 *   <ry-filter-option value="enterprise">Enterprise</ry-filter-option>
 * </ry-filter>
 * ```
 *
 * @example sizes
 * ```html
 * <ry-filter name="a" size="xs">
 *   <ry-filter-option value="1">One</ry-filter-option>
 *   <ry-filter-option value="2">Two</ry-filter-option>
 * </ry-filter>
 * <ry-filter name="b" size="lg">
 *   <ry-filter-option value="1">One</ry-filter-option>
 *   <ry-filter-option value="2">Two</ry-filter-option>
 * </ry-filter>
 * ```
 *
 * @example custom-reset-label
 * ```html
 * <ry-filter name="c" reset-label="All">
 *   <ry-filter-option value="active">Active</ry-filter-option>
 *   <ry-filter-option value="archived">Archived</ry-filter-option>
 * </ry-filter>
 * ```
 *
 * @tutorial renders-options-and-reset
 * ```js
 * const el = mount(`
 *   <ry-filter name="f">
 *     <ry-filter-option value="a">A</ry-filter-option>
 *     <ry-filter-option value="b">B</ry-filter-option>
 *   </ry-filter>
 * `);
 * await expectAttr(el, 'role', 'radiogroup');
 * const options = el.querySelectorAll('[data-ry-target="option"]');
 * await expectEqual(options.length, 2, 'one chip per option');
 * await expectEqual(el.querySelectorAll('[data-ry-target="reset"]').length, 1, 'one reset chip');
 * await expectAttr(el, 'data-ry-state', 'idle');
 * ```
 *
 * @tutorial select-hides-siblings-and-shows-reset
 * ```js
 * const el = mount(`
 *   <ry-filter name="f">
 *     <ry-filter-option value="a">A</ry-filter-option>
 *     <ry-filter-option value="b">B</ry-filter-option>
 *     <ry-filter-option value="c">C</ry-filter-option>
 *   </ry-filter>
 * `);
 * const [a, b, c] = el.querySelectorAll('[data-ry-target="option"]');
 * a.click();
 * await expectEqual(el.value, 'a');
 * await expectAttr(el, 'data-ry-state', 'filtered');
 * await expectAttr(a, 'aria-checked', 'true');
 * await expectAttr(b, 'data-ry-state', 'collapsed', 'sibling b collapsed');
 * await expectAttr(c, 'data-ry-state', 'collapsed', 'sibling c collapsed');
 * await expectAttr(a, 'data-ry-state', 'selected', 'a stays visible');
 * ```
 *
 * @tutorial reset-restores-all
 * ```js
 * // Symmetric inverse of select-hides-siblings: reset brings everything back.
 * const el = mount(`
 *   <ry-filter name="f" value="a">
 *     <ry-filter-option value="a">A</ry-filter-option>
 *     <ry-filter-option value="b">B</ry-filter-option>
 *   </ry-filter>
 * `);
 * const reset = el.querySelector('[data-ry-target="reset"]');
 * const [a, b] = el.querySelectorAll('[data-ry-target="option"]');
 * await expectAttr(el, 'data-ry-state', 'filtered', 'preselected = filtered');
 * reset.click();
 * await expectEqual(el.value, '');
 * await expectAttr(el, 'data-ry-state', 'idle');
 * await expectAttr(a, 'aria-checked', 'false');
 * await expectAttr(b, 'data-ry-state', 'idle', 'b restored');
 * ```
 *
 * @tutorial ry-change-fires-value-then-null
 * ```js
 * const el = mount(`
 *   <ry-filter name="f">
 *     <ry-filter-option value="a">A</ry-filter-option>
 *     <ry-filter-option value="b">B</ry-filter-option>
 *   </ry-filter>
 * `);
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail.value));
 * const [a] = el.querySelectorAll('[data-ry-target="option"]');
 * a.click();
 * el.reset();
 * await expectEqual(fired.length, 2);
 * await expectEqual(fired[0], 'a', 'select emits the value');
 * await expectEqual(fired[1], null, 'reset emits null');
 * ```
 *
 * @tutorial reselect-is-noop
 * ```js
 * // Wrong assumption: clicking the active chip toggles it off. It does NOT —
 * // radio-group semantics. No transition, no event.
 * const el = mount(`
 *   <ry-filter name="f">
 *     <ry-filter-option value="a">A</ry-filter-option>
 *     <ry-filter-option value="b">B</ry-filter-option>
 *   </ry-filter>
 * `);
 * const fired = [];
 * el.addEventListener('ry:change', () => fired.push(1));
 * const [a] = el.querySelectorAll('[data-ry-target="option"]');
 * a.click();
 * a.click(); // re-click the already-selected chip
 * await expectEqual(el.value, 'a', 'still selected');
 * await expectEqual(fired.length, 1, 'only the first click emitted');
 * ```
 *
 * @tutorial keyboard-navigates-and-selects
 * ```js
 * const el = mount(`
 *   <ry-filter name="f">
 *     <ry-filter-option value="a">A</ry-filter-option>
 *     <ry-filter-option value="b">B</ry-filter-option>
 *     <ry-filter-option value="c">C</ry-filter-option>
 *   </ry-filter>
 * `);
 * const [a, b] = el.querySelectorAll('[data-ry-target="option"]');
 * a.focus();
 * a.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * // Focus moved to b; select it with Enter.
 * b.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectEqual(el.value, 'b', 'Enter on focused option selects it');
 * await expectAttr(el, 'data-ry-state', 'filtered');
 * ```
 *
 * @tutorial escape-resets
 * ```js
 * // Symmetric to keyboard-selects: Escape is the keyboard reset.
 * const el = mount(`
 *   <ry-filter name="f" value="a">
 *     <ry-filter-option value="a">A</ry-filter-option>
 *     <ry-filter-option value="b">B</ry-filter-option>
 *   </ry-filter>
 * `);
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
 * await expectEqual(el.value, '');
 * await expectAttr(el, 'data-ry-state', 'idle');
 * ```
 *
 * @tutorial programmatic-value-setter-fires
 * ```js
 * // Wrong assumption: setting .value silently updates (like a native input).
 * // ry-filter's value setter DOES fire ry:change (it is the canonical
 * // selection API), mirroring a click.
 * const el = mount(`
 *   <ry-filter name="f">
 *     <ry-filter-option value="a">A</ry-filter-option>
 *     <ry-filter-option value="b">B</ry-filter-option>
 *   </ry-filter>
 * `);
 * const fired = [];
 * el.addEventListener('ry:change', (e) => fired.push(e.detail.value));
 * el.value = 'b';
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0], 'b');
 * await expectAttr(el.querySelectorAll('[data-ry-target="option"]')[1], 'aria-checked', 'true');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange keeps the current selection and
 * // ry:change stays silent — for select AND reset.
 * const el = mount(`
 *   <ry-filter name="f">
 *     <ry-filter-option value="a">A</ry-filter-option>
 *     <ry-filter-option value="b">B</ry-filter-option>
 *   </ry-filter>
 * `);
 * const [a] = el.querySelectorAll('[data-ry-target="option"]');
 * const changed = [];
 * el.addEventListener('ry:change', (e) => changed.push(e.detail.value));
 * el.addEventListener('ry:beforechange', (e) => {
 *   if (e.detail.value === 'a' && e.detail.previous === null) e.preventDefault();
 * }, { once: true });
 * a.click();
 * await expectEqual(el.value, '', 'vetoed select leaves nothing selected');
 * await expectAttr(el, 'data-ry-state', 'idle');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * a.click();
 * await expectEqual(el.value, 'a', 'without the veto, the click selects');
 * await expectEqual(changed[0], 'a');
 * // Reset is a transition too, and vetoable the same way.
 * el.addEventListener('ry:beforechange', (e) => e.preventDefault(), { once: true });
 * el.reset();
 * await expectEqual(el.value, 'a', 'vetoed reset keeps the selection');
 * await expectEqual(changed.length, 1, 'no ry:change for the vetoed reset');
 * el.reset();
 * await expectEqual(el.value, '', 'without the veto, reset clears');
 * await expectEqual(changed[1], null);
 * ```
 *
 * @see ry-toggle-button
 * @see ry-radio-group
 * @see ry-button-group
 */

import { RyElement } from '../core/ry-element.js';

interface FilterBeforeChangeDetail {
  value: string | null;
  previous: string | null;
}

interface FilterChangeDetail {
  value: string | null;
}

/**
 * Declarative option marker. Read once by the parent <ry-filter> at setup and
 * replaced by a managed chip button — it never renders on its own.
 *
 * @component ry-filter-option
 * @category  form
 * @summary   Declarative child of <ry-filter>. Carries a `value` attribute and
 *            label text; the parent converts it into a selectable chip.
 * @attribute value  string   Identifying value emitted in ry:change when this chip is selected.
 * @content   The chip's visible label (free-form text/inline markup).
 */
export class RyFilterOption extends RyElement {
  get value(): string {
    return this.getAttribute('value') ?? '';
  }
  // No setup() — the parent consumes this element. If it ever connects on its
  // own (not inside a ry-filter), it simply renders its children inline.
}

export class RyFilter extends RyElement {
  static observedAttributes = ['value', 'size', 'reset-label', 'name'] as const;

  #built = false;

  setup(): void {
    this.setAttribute('role', 'radiogroup');

    // Read declarative <ry-filter-option> children ONCE, then render managed
    // chip markup. After this, the original option elements are gone.
    const initial = this.#readOptions();
    this.#render(initial);
    this.#built = true;

    // Apply any preselected value from the attribute (collapses siblings).
    const preselect = this.getAttribute('value');
    if (preselect) {
      this.#select(preselect, { emit: false });
    } else {
      this.#setState('idle');
    }

    this.on(this, 'click', this.#handleClick);
    this.on(this, 'keydown', this.#handleKeydown);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.#built) return;
    if (name === 'value') {
      // Reflective updates from outside: select / clear without re-emitting via
      // the attribute path. The .value SETTER handles emission; attribute pokes
      // here only re-sync the visual state.
      if (newValue) {
        this.#select(newValue, { emit: false });
      } else {
        this.#clear({ emit: false });
      }
    }
  }

  // ─── Rendering ──────────────────────────────────────────────────────

  #readOptions(): { value: string; label: string }[] {
    const opts = this.$$<RyFilterOption>('ry-filter-option');
    return opts.map(o => ({
      value: o.getAttribute('value') ?? '',
      label: o.innerHTML.trim() || o.getAttribute('value') || '',
    }));
  }

  #render(options: { value: string; label: string }[]): void {
    const resetLabel = this.getAttribute('reset-label') ?? '×'; // ×
    const parts: string[] = [];
    for (const { value, label } of options) {
      parts.push(
        `<button type="button" class="ry-filter__option" data-ry-target="option"` +
        ` value="${escapeAttr(value)}" role="radio" aria-checked="false"` +
        ` data-ry-state="idle" tabindex="0">${label}</button>`
      );
    }
    parts.push(
      `<button type="button" class="ry-filter__reset" data-ry-target="reset"` +
      ` aria-label="Clear filter" tabindex="-1" data-ry-state="idle">${escapeText(resetLabel)}</button>`
    );
    this.innerHTML = parts.join('');
  }

  // ─── Option helpers ─────────────────────────────────────────────────

  #options(): HTMLButtonElement[] {
    return this.$$<HTMLButtonElement>('[data-ry-target="option"]');
  }

  #reset(): HTMLButtonElement | null {
    return this.$<HTMLButtonElement>('[data-ry-target="reset"]');
  }

  #visibleOptions(): HTMLButtonElement[] {
    return this.#options().filter(o => o.dataset.ryState !== 'collapsed');
  }

  #setState(state: 'idle' | 'filtered'): void {
    this.dataset.ryState = state;
    const reset = this.#reset();
    if (reset) {
      reset.dataset.ryState = state === 'filtered' ? 'visible' : 'idle';
      reset.tabIndex = state === 'filtered' ? 0 : -1;
    }
  }

  // ─── Selection ──────────────────────────────────────────────────────

  /**
   * Select the option whose [value] matches. Collapses siblings, reveals reset.
   * @param emit  whether to fire ry:change (false for attribute/init sync).
   */
  #select(value: string, opts: { emit: boolean }): void {
    const options = this.#options();
    const target = options.find(o => o.getAttribute('value') === value);
    if (!target) return;

    const previous = this.getAttribute('value') ?? '';
    if (previous === value) {
      // No transition — but still make sure visual state is coherent.
      this.#applySelectionVisual(target, options);
      return;
    }

    if (opts.emit && !this.emit<FilterBeforeChangeDetail>('beforechange', { value, previous: previous || null })) return;

    this.#applySelectionVisual(target, options);

    // Reflect to attribute WITHOUT re-triggering emission via attr callback:
    // set #built guard already prevents double work because we pass emit ourselves.
    if (this.getAttribute('value') !== value) {
      // Suppress attribute-callback re-entry by temporarily marking built false.
      const wasBuilt = this.#built;
      this.#built = false;
      this.setAttribute('value', value);
      this.#built = wasBuilt;
    }

    if (opts.emit) {
      this.emit<FilterChangeDetail>('change', { value });
    }
  }

  #applySelectionVisual(target: HTMLButtonElement, options: HTMLButtonElement[]): void {
    for (const o of options) {
      if (o === target) {
        o.dataset.ryState = 'selected';
        o.setAttribute('aria-checked', 'true');
        o.tabIndex = 0;
      } else {
        o.dataset.ryState = 'collapsed';
        o.setAttribute('aria-checked', 'false');
        o.tabIndex = -1;
      }
    }
    this.#setState('filtered');
  }

  /**
   * Clear the selection: restore all chips, hide reset.
   * @param emit  whether to fire ry:change with { value: null }.
   */
  #clear(opts: { emit: boolean }): void {
    const current = this.getAttribute('value') ?? '';
    const had = current !== '';
    if (opts.emit && had && !this.emit<FilterBeforeChangeDetail>('beforechange', { value: null, previous: current })) return;
    for (const o of this.#options()) {
      o.dataset.ryState = 'idle';
      o.setAttribute('aria-checked', 'false');
      o.tabIndex = 0;
    }
    this.#setState('idle');

    if (this.getAttribute('value')) {
      const wasBuilt = this.#built;
      this.#built = false;
      this.removeAttribute('value');
      this.#built = wasBuilt;
    }

    if (opts.emit && had) {
      this.emit<FilterChangeDetail>('change', { value: null });
    }
  }

  // ─── Events ─────────────────────────────────────────────────────────

  #handleClick = (event: MouseEvent): void => {
    const targetEl = event.target as HTMLElement | null;
    if (!targetEl) return;

    const reset = targetEl.closest('[data-ry-target="reset"]');
    if (reset && this.contains(reset)) {
      this.reset();
      return;
    }

    const option = targetEl.closest('[data-ry-target="option"]') as HTMLButtonElement | null;
    if (option && this.contains(option)) {
      const value = option.getAttribute('value') ?? '';
      // Re-selecting the active chip is a no-op (radio-group semantics).
      if ((this.getAttribute('value') ?? '') === value) return;
      this.#select(value, { emit: true });
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    const key = event.key;

    if (key === 'Escape') {
      event.preventDefault();
      this.reset();
      return;
    }

    if (key === 'Enter' || key === ' ') {
      const active = document.activeElement;
      const option = (active && this.contains(active))
        ? (active as HTMLElement).closest('[data-ry-target="option"]') as HTMLButtonElement | null
        : null;
      if (option) {
        event.preventDefault();
        const value = option.getAttribute('value') ?? '';
        if ((this.getAttribute('value') ?? '') !== value) {
          this.#select(value, { emit: true });
        }
      }
      return;
    }

    if (
      key !== 'ArrowRight' && key !== 'ArrowDown' &&
      key !== 'ArrowLeft' && key !== 'ArrowUp' &&
      key !== 'Home' && key !== 'End'
    ) return;

    const visible = this.#visibleOptions();
    if (visible.length === 0) return;

    const active = document.activeElement;
    let currentIdx = visible.findIndex(o => o === active || o.contains(active as Node));
    if (currentIdx === -1) currentIdx = 0;

    let nextIdx = currentIdx;
    if (key === 'ArrowRight' || key === 'ArrowDown') {
      nextIdx = (currentIdx + 1) % visible.length;
    } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
      nextIdx = (currentIdx - 1 + visible.length) % visible.length;
    } else if (key === 'Home') {
      nextIdx = 0;
    } else if (key === 'End') {
      nextIdx = visible.length - 1;
    }

    event.preventDefault();
    visible[nextIdx]?.focus();
  };

  // ─── Public API ─────────────────────────────────────────────────────

  /** Clear the selection + restore all chips. Fires ry:change { value: null } if something was selected. */
  reset(): void {
    this.#clear({ emit: true });
  }

  get value(): string {
    return this.getAttribute('value') ?? '';
  }

  set value(val: string) {
    if (val) {
      if ((this.getAttribute('value') ?? '') === val) return;
      this.#select(val, { emit: true });
    } else {
      this.reset();
    }
  }

  get name(): string {
    return this.getAttribute('name') ?? '';
  }

  set name(val: string) {
    if (val) this.setAttribute('name', val);
    else this.removeAttribute('name');
  }
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

customElements.define('ry-filter-option', RyFilterOption);
customElements.define('ry-filter', RyFilter);
